/* ============================================
   TABLE ROOT
   ============================================ */
.tableRoot {
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
	/* `--col-{id}-size` variables are injected via inline style by useColumnSizeVars; */
	/* `--entries-grid-template` is injected by EntriesTable using useGridTemplateColumns. */
}

/* Cell padding (consumer-controlled via `cellPadding` prop). */
.tableRoot {
	--entries-cell-padding: 0px;
}

/* ============================================
   HEADER
   ============================================ */
.headerRow {
	width: 100%;
	display: grid;
	grid-template-columns: var(--entries-grid-template);
	align-items: stretch;
	font-size: 0.8125rem;
	padding: 10px 20px;
	gap: 8px;
	font-weight: 600;
	flex-shrink: 0;
	color: var(--brand-dark-green);
	background-color: var(--secondary);
	border-radius: var(--radius) var(--radius) 0 0;
	border-bottom: 1px solid var(--border);
	user-select: none;
	box-sizing: border-box;
}

:global(.dark) .headerRow {
	background-color: var(--card);
	color: var(--foreground);
}

.headerCell {
	position: relative;
	display: flex;
	align-items: center;
	text-align: left;
	min-width: 0;
	overflow: hidden;
	padding-right: var(--entries-cell-padding);
}

.headerCellSortable {
	cursor: pointer;
}

.headerCellUnits {
	justify-content: flex-end;
}

/* ============================================
   SCROLL CONTAINER
   ============================================ */
.scrollContainer {
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: stretch;
	box-sizing: border-box;
	justify-content: flex-start;
	flex-grow: 1;
	overflow-y: auto;
	overflow-x: hidden;
	min-width: 650px;
}

/* ============================================
   ENTRY ROW
   ============================================ */
.row {
	width: 100%;
	display: grid;
	grid-template-columns: var(--entries-grid-template);
	align-items: center;
	gap: 8px;
	min-height: 56px;
	padding: 10px 20px;
	font-size: 0.8125rem;
	font-weight: 400;
	color: var(--foreground);
	box-sizing: border-box;
	cursor: default;
	border-bottom: 1px solid color-mix(in oklch, var(--border) 30%, transparent);
	transition: background-color 0.15s ease;
}

:global([data-palette="default"]) .row {
	border-bottom: 1px solid var(--border);
}

:global(.dark) .row {
	background-color: var(--background);
	color: var(--foreground);
}

.row:hover {
	background-color: color-mix(in oklch, var(--secondary) 60%, transparent);
}

:global(.dark) .row:hover {
	background-color: var(--card);
}

:global(.dark[data-palette="default"]) .row:hover {
	background-color: #262626;
}

.cell {
	display: flex;
	align-items: center;
	min-width: 0;
	overflow: hidden;
	white-space: normal;
	word-break: break-word;
	padding-right: var(--entries-cell-padding);
}

/* `nowrap` variant — for non-wrapping content like the date column */
.cellNowrap {
	white-space: nowrap;
	text-overflow: ellipsis;
}

/* Right-align units cell */
.cellUnits {
	justify-content: flex-end;
}

/* Row visual states — left "ribbon" indicators. Precedence picked by the
   Row component: failed > incomplete > warning (unresolved feedback). */
.failedRow {
	border-left: 3px solid var(--destructive);
}

.incompleteRow {
	border-left: 3px solid var(--destructive);
}

.warningRow {
	border-left: 3px solid var(--warning);
}

.exportingRow {
	opacity: 0.55;
	pointer-events: none;
	cursor: not-allowed;
}

.mutatingRow {
	opacity: 0.5;
	pointer-events: none;
	transition: opacity 0.2s;
}

/* ============================================
   CELL-CONTENT BITS (preserved from old module)
   ============================================ */
.dateTimeWrapper {
	display: flex;
	flex-direction: column;
	gap: 1px;
	line-height: 1.3;
}

.timeRange {
	font-size: 0.6875rem;
	color: var(--muted-foreground);
	white-space: nowrap;
}

.appLogo {
	width: 30px;
	height: 30px;
}

.feedbackWarning {
	flex-shrink: 0;
	display: inline-flex;
	align-items: center;
	cursor: pointer;
}

.warningIcon {
	width: 16px;
	height: 16px;
	color: #d97706; /* amber-600 */
}

:global(.dark) .warningIcon {
	color: #fbbf24; /* amber-400 */
}

.checkboxHitArea {
	display: inline-flex;
	align-items: center;
	padding: 6px 8px;
	margin: -6px -8px;
	cursor: pointer;
	user-select: none;
}

.selectionCount {
	font-size: 0.6875rem;
	font-weight: 600;
	color: var(--brand-dark-teal, hsl(var(--primary)));
	white-space: nowrap;
}

/* ============================================
   UNITS / STATUS BADGES
   ============================================ */
.unitsBadge {
	display: flex;
	justify-content: center;
	align-items: center;
	border-radius: 7px;
	color: var(--background);
	background-color: var(--brand-dark-green);
	border: 1px solid var(--background);
	padding: 5px 10px;
	width: auto;
	min-width: 50px;
	font-size: 0.8125rem;
	font-weight: 525;
}

:global(.dark) .unitsBadge {
	color: #ffffff;
	background-color: var(--brand-dark-green);
	border: none;
}

:global([data-palette="default"]) .unitsBadge {
	background-color: var(--primary);
}

:global(.dark[data-palette="default"]) .unitsBadge {
	background-color: var(--brand-dark-green);
}

.failedBadge {
	font-size: 0.6875rem;
	font-weight: 600;
	padding: 4px 10px;
	border-radius: 100px;
	display: flex;
	justify-content: center;
	align-items: center;
	min-width: 70px;
	color: var(--destructive);
	background-color: color-mix(in oklch, var(--destructive) 10%, transparent);
	border: 1px solid color-mix(in oklch, var(--destructive) 50%, transparent);
}

:global(.dark) .failedBadge {
	color: var(--destructive);
	background-color: color-mix(in oklch, var(--destructive) 10%, transparent);
	border: 1px solid var(--border);
}

.exportedBadge {
	font-size: 0.6875rem;
	font-weight: 600;
	padding: 4px 10px;
	border-radius: 100px;
	display: flex;
	justify-content: center;
	align-items: center;
	min-width: 70px;
	color: var(--success);
	background-color: color-mix(in oklch, var(--success-soft) 10%, transparent);
	border: 1px solid color-mix(in oklch, var(--success) 50%, transparent);
}

:global(.dark) .exportedBadge {
	color: var(--success);
	background-color: color-mix(in oklch, var(--success-soft) 10%, transparent);
	border: 1px solid var(--border);
}

.incompleteBadge {
	font-size: 0.6875rem;
	font-weight: 600;
	padding: 4px 10px;
	border-radius: 100px;
	display: flex;
	justify-content: center;
	align-items: center;
	min-width: 70px;
	color: var(--destructive);
	background-color: color-mix(in oklch, var(--destructive) 10%, transparent);
	border: 1px solid color-mix(in oklch, var(--destructive) 50%, transparent);
}

.trackedBadge {
	font-size: 0.6875rem;
	font-weight: 600;
	padding: 4px 10px;
	border-radius: 100px;
	display: flex;
	justify-content: center;
	align-items: center;
	min-width: 70px;
	color: var(--warning);
	background-color: color-mix(in oklch, var(--warning-soft) 10%, transparent);
	border: 1px solid color-mix(in oklch, var(--warning) 50%, transparent);
}

:global(.dark) .trackedBadge {
	color: var(--warning);
	background-color: color-mix(in oklch, var(--warning-soft) 10%, transparent);
	border: 1px solid var(--border);
}

.exportingBadge {
	font-size: 0.6875rem;
	font-weight: 600;
	padding: 4px 10px;
	border-radius: 100px;
	display: flex;
	justify-content: center;
	align-items: center;
	min-width: 70px;
	color: var(--brand-teal, hsl(var(--primary)));
	background-color: color-mix(
		in oklch,
		var(--brand-teal, hsl(var(--primary))) 10%,
		transparent
	);
	border: 1px solid
		color-mix(in oklch, var(--brand-teal, hsl(var(--primary))) 50%, transparent);
}

:global(.dark) .exportingBadge {
	color: var(--brand-teal, hsl(var(--primary)));
	background-color: color-mix(
		in oklch,
		var(--brand-teal, hsl(var(--primary))) 10%,
		transparent
	);
	border: 1px solid var(--border);
}

/* ============================================
   DATE GROUP HEADER
   ============================================ */
.dateGroupHeader {
	display: flex;
	flex-direction: row;
	width: 100%;
	justify-content: space-between;
	align-items: center;
	padding: 4px 15px 4px 20px;
	background-color: var(--secondary);
	border-top: 1px solid var(--border);
}

@media (max-width: 1000px) {
	.dateGroupHeader {
		justify-content: flex-start;
	}
}

:global(.dark) .dateGroupHeader {
	background: var(--card);
	border-top: 1px solid var(--border);
}

.dateText {
	display: flex;
	justify-content: left;
	align-items: center;
	font-size: 0.75rem;
	font-weight: 600;
	letter-spacing: 0.03em;
	text-transform: uppercase;
	color: var(--muted-foreground);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	min-height: 18px;
}

.groupTotal {
	font-size: 0.75rem;
	font-weight: 600;
	letter-spacing: 0.03em;
	text-transform: uppercase;
	color: var(--muted-foreground);
	white-space: nowrap;
}

/* ============================================
   EMPTY STATE
   ============================================ */
.emptyState {
	height: 100%;
	width: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
}

.emptyText {
	display: flex;
	justify-content: center;
	align-items: center;
	font-size: 0.875rem;
	color: var(--muted-foreground);
}

:global(.dark) .emptyText {
	color: var(--muted-foreground);
}

/* ============================================
   MOBILE (≤ 650px)
   Force-hide the optional columns and let matter + narrative + units stretch.
   Resize handles are still hittable; the user can show columns back via the
   visibility picker even on mobile.
   ============================================ */
@media screen and (max-width: 650px) {
	.scrollContainer {
		min-width: unset;
		flex: 1;
		height: 100%;
		-webkit-overflow-scrolling: touch;
	}

	.headerRow {
		position: sticky;
		top: 0;
		z-index: 10;
	}
}
